import { useEffect, useState } from "react";
import {baseURL} from "../utils/common";
import axios from "axios";
import {useNavigate} from "react-router";

const fieldOrder = [
    "client_type",
    "name",
    "bin_code",
    "address",
    "contact_name",
    "contact_phone_number",
    "contact_email",
];

export const ClientsPage = () => {
    const history = useNavigate();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const CLIENTS_LIST_URL = `${baseURL}/orders/clients/list/`;
    const CLIENT_CREATE_URL = `${baseURL}/orders/client/create/`;

    const [clients, setClients] = useState([]);
    const [count, setCount] = useState(0);

    const [filters, setFilters] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(null);

    const [q, setQ] = useState("");
    const [clientType, setClientType] = useState("");
    const [ordering, setOrdering] = useState("-updated_at");
    const [limit, setLimit] = useState(20);

    const [loading, setLoading] = useState(false);
    const [moreLoading, setMoreLoading] = useState(false);
    const [error, setError] = useState("");

    const [createOpen, setCreateOpen] = useState(false);
    const [createFields, setCreateFields] = useState(null);
    const [createForm, setCreateForm] = useState({});
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState("");

    const loadClients = async (reset = true) => {
        if (reset) {
            setLoading(true);
        } else {
            setMoreLoading(true);
        }

        setError("");

        const offset = reset ? 0 : nextOffset;

        const params = {
            limit,
            offset: offset || 0,
        };

        if (q.trim()) {
            params.q = q.trim();
        }

        if (clientType) {
            params.client_type = clientType;
        }

        if (ordering) {
            params.ordering = ordering;
        }

        try {
            const response = await axios.get(CLIENTS_LIST_URL, {
                params,
            });

            const data = response.data;

            if (!data.success) {
                setError(data.message || "Не удалось загрузить клиентов");
                return;
            }

            if (reset) {
                setClients(data.results || []);
            } else {
                setClients((prev) => [...prev, ...(data.results || [])]);
            }

            setCount(data.count || 0);
            setFilters(data.filters || null);
            setHasMore(Boolean(data.has_more));
            setNextOffset(data.next_offset);
        } catch (e) {
            setError("Ошибка соединения с сервером");
        } finally {
            setLoading(false);
            setMoreLoading(false);
        }
    };

    const openCreateModal = async () => {
        setCreateOpen(true);
        setCreateError("");

        if (createFields) {
            return;
        }

        try {
            const response = await axios.get(CLIENT_CREATE_URL);
            const data = response.data;

            if (!data.success) {
                setCreateError(data.message || "Не удалось получить параметры формы");
                return;
            }

            const fields = data.fields || {};
            const form = {};

            fieldOrder.forEach((key) => {
                if (fields[key]) {
                    form[key] = fields[key].default || "";
                }
            });

            setCreateFields(fields);
            setCreateForm(form);
        } catch (e) {
            setCreateError(e.response?.data?.message || "Ошибка соединения с сервером");
        }
    };

    const closeCreateModal = () => {
        setCreateOpen(false);
        setCreateError("");
    };

    const changeCreateField = (key, value) => {
        setCreateForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const createClient = async (e) => {
        e.preventDefault();

        if (createLoading) {
            return;
        }

        setCreateLoading(true);
        setCreateError("");

        try {
            const response = await axios.post(CLIENT_CREATE_URL, createForm);
            const data = response.data;

            if (!data.success) {
                setCreateError(data.message || "Не удалось создать клиента");
                return;
            }

            setCreateOpen(false);
            setCreateForm({});
            setCreateFields(null);

            await loadClients(true);
        } catch (e) {
            setCreateError(e.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setCreateLoading(false);
        }
    };

    const goToClientDetails = (clientId) => {
        history(`/clients/${clientId}`);
    };

    const renderFiltersContent = () => (
        <div className="clients-filter-panel">
            <div className="clients-filter-field clients-filter-search">
                <span>Поиск</span>
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={filters?.q?.placeholder || "Клиент, БИН/ИИН, телефон"}
                />
            </div>

            <div className="clients-filter-field">
                <span>Тип клиента</span>
                <select value={clientType} onChange={(e) => setClientType(e.target.value)}>
                    <option value="">Все</option>

                    {filters?.client_type?.options?.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className="clients-filter-field">
                <span>Сортировка</span>
                <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
                    {filters?.ordering?.options?.map((item) => (
                        <option key={item.value} value={item.value}>
                            {item.label}
                        </option>
                    ))}

                    {!filters?.ordering?.options && (
                        <>
                            <option value="-updated_at">Сначала новые</option>
                            <option value="updated_at">Сначала старые</option>
                        </>
                    )}
                </select>
            </div>

            <div className="clients-filter-field clients-filter-limit">
                <span>Показать</span>
                <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                </select>
            </div>

            <button
                className="clients-filter-reset"
                type="button"
                onClick={() => {
                    setQ("");
                    setClientType("");
                    setOrdering("-updated_at");
                    setLimit(20);
                }}
            >
                Сбросить
            </button>
        </div>
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            loadClients(true);
        }, 350);

        return () => clearTimeout(timer);
    }, [q, clientType, ordering, limit]);

    return (
        <section className="clients-page">
            <div className="clients-head">
                <div>
                    <h1>Клиенты</h1>
                    <p>База клиентов Luminopt, контакты и история заказов</p>
                </div>

                <button className="clients-create-btn" type="button" onClick={openCreateModal}>
                    + Добавить клиента
                </button>
            </div>

            <div className="clients-desktop-filters">
                {renderFiltersContent()}
            </div>

            <div className="clients-mobile-filter-row">
                <button type="button" onClick={() => setFiltersOpen(true)}>
                    Фильтры
                </button>

                <span>
    {count} клиентов
  </span>
            </div>

            {filtersOpen && (
                <div className="clients-filter-modal">
                    <button
                        className="clients-filter-modal-bg"
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                    />

                    <div className="clients-filter-modal-card">
                        <div className="clients-filter-modal-head">
                            <div>
                                <h2>Фильтры</h2>
                                <p>Поиск, тип клиента и сортировка</p>
                            </div>

                            <button type="button" onClick={() => setFiltersOpen(false)}>
                                ✕
                            </button>
                        </div>

                        {renderFiltersContent}

                        <button
                            className="clients-filter-apply"
                            type="button"
                            onClick={() => setFiltersOpen(false)}
                        >
                            Применить
                        </button>
                    </div>
                </div>
            )}

            <div className="clients-card">
                <div className="clients-card-top">
                    <strong>Всего клиентов: {count}</strong>

                    {loading && <span>Загрузка...</span>}
                </div>

                {error && <div className="clients-error">{error}</div>}

                {!loading && clients.length === 0 && (
                    <div className="clients-empty">Клиенты не найдены</div>
                )}

                {clients.length > 0 && (
                    <div className="clients-list">
                        {clients.map((client) => (
                            <article className="client-card" key={client.id}>
                                <div className="client-card-main">
                                    <div>
                                        <span className="client-card-label">Клиент</span>
                                        <h3>{client.name || "—"}</h3>
                                        <p>{client.address || "—"}</p>
                                    </div>

                                    <span className="client-card-type">
          {client.client_type || "—"}
        </span>
                                </div>

                                <div className="client-card-grid">
                                    <div className="client-card-item">
                                        <span>БИН/ИИН</span>
                                        <strong>{client.bin_code || "—"}</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>Контактное лицо</span>
                                        <strong>{client.contact?.name || "—"}</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>Телефон</span>

                                        {client.contact?.phone_number && client.contact.phone_number !== "—" ? (
                                            <a href={`tel:${client.contact.phone_number}`}>
                                                {client.contact.phone_number}
                                            </a>
                                        ) : (
                                            <strong>—</strong>
                                        )}
                                    </div>

                                    <div className="client-card-item">
                                        <span>Почта</span>
                                        <strong>{client.contact?.email || "—"}</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>Количество заказов</span>
                                        <strong>{client.orders?.count ?? 0}</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>Сумма заказов</span>
                                        <strong>{Number(client.orders?.pay_sum || 0).toLocaleString("ru-RU")} ₸</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>Обновлено</span>
                                        <strong>{client.updated_at || "—"}</strong>
                                    </div>

                                    <div className="client-card-item">
                                        <span>ID клиента</span>
                                        <strong>#{client.id}</strong>
                                    </div>
                                </div>
                                <div className="client-card-actions">
                                    <button
                                        type="button"
                                        onClick={() => goToClientDetails(client.id)}
                                    >
                                        Подробнее
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {hasMore && (
                    <div className="clients-more">
                        <button type="button" onClick={() => loadClients(false)} disabled={moreLoading}>
                            {moreLoading ? "Загрузка..." : "Показать ещё"}
                        </button>
                    </div>
                )}
            </div>

            {createOpen && (
                <div className="clients-modal">
                    <button className="clients-modal-bg" type="button" onClick={closeCreateModal} />

                    <div className="clients-modal-card">
                        <div className="clients-modal-head">
                            <div>
                                <h2>Добавить клиента</h2>
                                <p>Создание клиента и контактного лица</p>
                            </div>

                            <button type="button" onClick={closeCreateModal}>
                                ✕
                            </button>
                        </div>

                        {createError && <div className="clients-error">{createError}</div>}

                        {!createFields && !createError && (
                            <div className="clients-empty">Загрузка формы...</div>
                        )}

                        {createFields && (
                            <form className="clients-form" onSubmit={createClient}>
                                {fieldOrder.map((key) => {
                                    const field = createFields[key];

                                    if (!field) {
                                        return null;
                                    }

                                    if (field.type === "select") {
                                        return (
                                            <label key={key}>
                        <span>
                          {field.label}
                            {field.required && <b>*</b>}
                        </span>

                                                <select
                                                    value={createForm[key] || ""}
                                                    onChange={(e) => changeCreateField(key, e.target.value)}
                                                >
                                                    {field.options?.map((item) => (
                                                        <option key={item.value} value={item.value}>
                                                            {item.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        );
                                    }

                                    if (field.type === "textarea") {
                                        return (
                                            <label key={key} className="clients-form-full">
                        <span>
                          {field.label}
                            {field.required && <b>*</b>}
                        </span>

                                                <textarea
                                                    value={createForm[key] || ""}
                                                    onChange={(e) => changeCreateField(key, e.target.value)}
                                                    placeholder={field.placeholder || ""}
                                                />
                                            </label>
                                        );
                                    }

                                    return (
                                        <label key={key}>
                      <span>
                        {field.label}
                          {field.required && <b>*</b>}
                      </span>

                                            <input
                                                type={field.type || "text"}
                                                value={createForm[key] || ""}
                                                onChange={(e) => changeCreateField(key, e.target.value)}
                                                placeholder={field.placeholder || ""}
                                            />
                                        </label>
                                    );
                                })}

                                <div className="clients-form-actions">
                                    <button type="button" onClick={closeCreateModal}>
                                        Отмена
                                    </button>

                                    <button type="submit" disabled={createLoading}>
                                        {createLoading ? "Сохраняем..." : "Сохранить клиента"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
};
